import { translateTemplateText, useLiquidGlassPress } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListCategoryWithExternalDescription } from '../../client/types';
import { getColor } from '../../helpers/color';
import { trackHealthLeeCrossSellShown } from '../../helpers/healthLee';
import { CategoryIcon } from '../common/CategoryIcon';
import { CommonIcon } from '../common/CommonIcon';
import { BorderLinearProgress } from './BorderLinearProgress';
import { healthLeeAchievementTemplateCategoryId } from './healthLeeAchievements';

const ROW_DIVIDER_BORDER = '1px solid color-mix(in srgb, var(--tg-theme-text-color) 16%, transparent)';

type AchievementCategoryItemProps = {
  category: ListCategoryWithExternalDescription;
  childrenMap: Record<string, ListCategoryWithExternalDescription[]>;
  expandedMap: Record<string, boolean>;
  isSubCategory?: boolean;
  showDivider?: boolean;
  externalDescription?: string;
  onCategoryClick: () => void;
  onToggleExpand: (categoryId: string) => void;
};

export const AchievementCategoryItem: FC<AchievementCategoryItemProps> = ({
  category,
  childrenMap,
  expandedMap,
  isSubCategory = false,
  showDivider = false,
  externalDescription,
  onCategoryClick,
  onToggleExpand
}) => {
  const { t } = useTranslation();
  const { icon, iconColor, name, id } = category;
  const displayName = translateTemplateText(t, category.templateId, 'name', name);
  const displayExternalDescription = translateTemplateText(t, category.templateId, 'description', externalDescription ?? '');

  const categoryTotal = category.achievementProgressTotal || 0;
  const categoryCompleted = category.achievementProgressCompleted || 0;

  const subCategories = childrenMap[id] || [];
  const hasSubCategories = subCategories.length > 0 && !isSubCategory;

  const subCategoriesTotal = !isSubCategory ?
    subCategories.reduce((sum, sub) => sum + (sub.achievementProgressTotal || 0), 0) :
    0;

  const subCategoriesCompleted = !isSubCategory ?
    subCategories.reduce((sum, sub) => sum + (sub.achievementProgressCompleted || 0), 0) :
    0;

  const totalAchievements = categoryTotal + subCategoriesTotal;
  const completedAchievements = categoryCompleted + subCategoriesCompleted;
  const progressPercentage = totalAchievements > 0 ? Math.round((completedAchievements / totalAchievements) * 100) : 0;
  const progressColor = category.progressColor ?? getColor(iconColor);
  const isExternalCategory = !!category.externalLink;

  useEffect(() => {
    if (category.templateId === healthLeeAchievementTemplateCategoryId || category.templateId?.includes('healthlee')) {
      trackHealthLeeCrossSellShown('achievement_category');
    }
  }, [category.templateId]);

  const expanded = !!expandedMap[id];

  const { pressed, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick } = useLiquidGlassPress({
    preventDefault: true,
    onActivate: () => {
      hapticFeedback.impactOccurred.ifAvailable('light');
      onCategoryClick();
    },
  });

  return (
    <Box
      component="button"
      type="button"
      aria-label={isExternalCategory ? displayExternalDescription : displayName}
      data-external-category={isExternalCategory ? true : undefined}
      data-template-category={category.templateId ?? undefined}
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      sx={{
        display: 'block',
        width: '100%',
        m: 0,
        p: 0,
        border: 'none',
        borderTop: showDivider ? ROW_DIVIDER_BORDER : 'none',
        background: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        bgcolor: pressed ? 'color-mix(in srgb, var(--tg-theme-text-color) 6%, transparent)' : 'transparent',
        transition: 'background-color 0.12s ease',
      }}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <Box paddingLeft={isSubCategory ? 3 : 2} py={1.5}>
          <Box
            sx={{
              width: isSubCategory ? 32 : 40,
              height: isSubCategory ? 32 : 40,
              borderRadius: isSubCategory ? 1 : 2,
              background: `${getColor(iconColor)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CategoryIcon
              icon={icon}
              iconColor={iconColor}
              customIcon={category.customIcon}
              type="filled"
              color="white"
              size={isSubCategory ? 20 : 28}
              sx={category.customIcon ? { width: '100%', height: '100%', borderRadius: 'inherit' } : undefined}
            />
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }} py={1.5} >
          <Box sx={{ maxWidth: '100%' }}>
            <Text weight={isSubCategory ? "1" : "2"} style={{
              color: 'var(--tg-theme-text-color)',
              fontSize: isSubCategory ? '13px' : '14px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              display: 'block'
            }}>{displayName}</Text>
          </Box>
          {isExternalCategory ? (
            <Box mt={0.5}>
              <Text style={{ color: 'var(--tg-theme-hint-color)', fontSize: '12px' }}>{displayExternalDescription}</Text>
            </Box>
          ) : (
            <Box mt={0.5} display="flex" alignItems="center" gap={1} sx={{ maxWidth: 300 }}>
              <Text style={{ color: 'var(--tg-theme-hint-color)', fontSize: '12px', whiteSpace: 'nowrap' }}>{t('achievement.progress')}</Text>
              <BorderLinearProgress
                variant="determinate"
                colorOverride={progressColor}
                value={progressPercentage}
                sx={{
                  mt: 0,
                  flex: 1,
                  height: 6,
                }} />
              <Text style={{ color: 'var(--tg-theme-text-color)', fontSize: '12px', minWidth: '35px', whiteSpace: 'nowrap' }}>{progressPercentage}%</Text>
            </Box>
          )}
        </Box>
        <Stack
          sx={{ alignSelf: 'stretch', width: '15%', alignItems: 'center', justifyContent: 'center', gap: 0.5, cursor: hasSubCategories ? 'pointer' : 'default' }}
          onClick={hasSubCategories ? (e) => {
            e.stopPropagation();
            hapticFeedback.impactOccurred.ifAvailable('light');
            onToggleExpand(id);
          } : undefined}
        >
          {isExternalCategory ? <CommonIcon icon="launch" type="filled" size={22} color="var(--tg-theme-hint-color)" /> : <Box display="flex" alignItems="baseline">
            <Text style={{ color: 'var(--tg-theme-text-color)', fontSize: '16px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{completedAchievements}</Text>
            <Text style={{ color: 'var(--tg-theme-hint-color)', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>/{totalAchievements}</Text>
          </Box>}
          {!isExternalCategory && hasSubCategories && (
            <CommonIcon
              icon={expanded ? 'expand_less' : 'expand_more'}
              type="filled"
              size={26}
              color="var(--tg-theme-hint-color)"
            />
          )}
        </Stack>
      </Box>
    </Box>
  );
};
